import { Routes } from '@angular/router';

import { AuthGuard } from './services/auth.guard';
import { NoAuthGuard } from './core/guard/no-auth.guard';
import { RoleType } from './core/services/role.service';
import { RoleGuard } from './core/guard/role.guard';
import { DashboardResolver } from './pages/dashboard/dashboard.resolver';
import { InvoicesResolver } from './pages/invoices/invoices.resolver';
import { ProjectResolver } from './pages/project/project.resolver';
import { ClientsResolver } from './pages/clients/clients.resolver';
import { UsersResolver } from './pages/users/users.resolver';
import { TimesheetResolver } from './pages/timesheets/timesheet.resolver';
import { FullLayoutComponent } from '../layout/layouts/full-layout.component';
import { BlankLayoutComponent } from '../layout/layouts/blank-layout.component';
import { AppErrorComponent } from './pages/authentication/error/error.component';
import { ChangePasswordComponent } from './pages/authentication/change-password/change-password.component';
import { PasswordResetComponent } from './pages/authentication/password-reset/password-reset.component';
import { AppSideLoginComponent } from './pages/authentication/side-login/side-login.component';

export const routes: Routes = [
  {
    path: '',
    component: FullLayoutComponent,
    canActivate: [AuthGuard],

    children: [
      {
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        canActivate: [RoleGuard],
        resolve: {
          dashboardData: DashboardResolver,
        },
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Dashboard',
          initialPath: 'dashboard',
          mode: 'browser',
        },
      },
      //projects
      {
        path: 'projects',
        canActivate: [RoleGuard],
        resolve: {
          projectData: ProjectResolver,
        },
        loadComponent: () =>
          import('./pages/project/project.component').then(
            (m) => m.ProjectComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Projects',
          initialPath: 'projects',
          mode: 'browser',
        },
      },
      {
        path: 'projects/add',
        canActivate: [RoleGuard],
        resolve: {
          projectData: ProjectResolver,
        },
        loadComponent: () =>
          import(
            './pages/project/project-create/project-create.component'
          ).then((m) => m.ProjectCreateComponent),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Create Project',
          initialPath: 'projects',
          mode: 'add',
          initialBreadcrumbName: 'Projects',
        },
      },
      {
        path: 'projects/:id',
        canActivate: [RoleGuard],
        resolve: {
          projectData: ProjectResolver,
        },
        loadComponent: () =>
          import(
            './pages/project/project-detail/project-detail.component'
          ).then((m) => m.ProjectDetailComponent),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Project Details',
          initialPath: 'projects',
          mode: 'browser',
          initialBreadcrumbName: 'Projects',
        },
      },
      {
        path: 'projects/edit/:id',
        canActivate: [RoleGuard],
        resolve: {
          projectData: ProjectResolver,
        },
        loadComponent: () =>
          import('./pages/project/project-edit/project-edit.component').then(
            (m) => m.ProjectEditComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Edit Project',
          initialPath: 'projects',
          mode: 'edit',
          initialBreadcrumbName: 'Projects',
        },
      },
      //clients
      {
        path: 'clients',
        canActivate: [RoleGuard],
        resolve: {
          clients: ClientsResolver,
        },
        loadComponent: () =>
          import('./pages/clients/clients.component').then(
            (m) => m.ClientsComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Clients',
          initialPath: 'clients',
          mode: 'browser',
        },
      },
      {
        path: 'clients/add',
        canActivate: [RoleGuard],

        loadComponent: () =>
          import('./pages/clients/client-create/client-create.component').then(
            (m) => m.ClientCreateComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Create Client',
          initialPath: 'clients',
          mode: 'add',
          initialBreadcrumbName: 'Clients',
        },
      },
      {
        path: 'clients/:id',
        canActivate: [RoleGuard],
        resolve: {
          client: ClientsResolver,
        },
        loadComponent: () =>
          import('./pages/clients/client-detail/client-detail.component').then(
            (m) => m.ClientDetailComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Client Details',
          initialPath: 'clients',
          mode: 'browser',
          initialBreadcrumbName: 'Clients',
        },
      },
      {
        path: 'clients/edit/:id',
        canActivate: [RoleGuard],
        resolve: {
          client: ClientsResolver,
        },
        loadComponent: () =>
          import('./pages/clients/client-edit/client-edit.component').then(
            (m) => m.ClientEditComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Edit Client',
          initialPath: 'clients',
          mode: 'edit',
          initialBreadcrumbName: 'Clients',
        },
      },

      //users
      {
        path: 'users',
        canActivate: [RoleGuard],
        resolve: {
          users: UsersResolver,
        },
        loadComponent: () =>
          import('./pages/users/users.component').then((m) => m.UsersComponent),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Users',
          initialPath: 'users',
          mode: 'browser',
        },
      },
      {
        path: 'users/add',
        canActivate: [RoleGuard],
        resolve: {
          UsersResolver,
        },
        loadComponent: () =>
          import('./pages/users/user-create/user-create.component').then(
            (m) => m.UserCreateComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Create User',
          initialPath: 'users',
          mode: 'add',
          initialBreadcrumbName: 'Users',
        },
      },
      {
        path: 'users/:id',
        canActivate: [RoleGuard],
        resolve: {
          UsersResolver,
        },
        loadComponent: () =>
          import('./pages/users/user-detail/user-detail.component').then(
            (m) => m.UserDetailComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'User Details',
          initialPath: 'users',
          mode: 'browser',
          initialBreadcrumbName: 'Users',
        },
      },
      {
        path: 'users/edit/:id',
        canActivate: [RoleGuard],
        resolve: {
          UsersResolver,
        },
        loadComponent: () =>
          import('./pages/users/user-edit/user-edit.component').then(
            (m) => m.UserEditComponent,
          ),
        data: {
          roles: [RoleType.Admin],
          breadcrumb: 'Edit User',
          initialPath: 'users',
          mode: 'edit',
          initialBreadcrumbName: 'Users',
        },
      },
      //invoices
      {
        path: 'invoices',
        canActivate: [RoleGuard],
        resolve: {
          invoiceData: InvoicesResolver,
        },
        loadComponent: () =>
          import('./pages/invoices/invoices.component').then(
            (m) => m.InvoicesComponent,
          ),
        data: {
          roles: [RoleType.Admin, RoleType.Client],
          breadcrumb: 'Invoices',
          initialPath: 'invoices',
          mode: 'browser',
        },
      },
      {
        path: 'invoices/:id',
        canActivate: [RoleGuard],
        resolve: {
          invoiceData: InvoicesResolver,
        },
        loadComponent: () =>
          import('./pages/invoices/invoice-detail.component').then(
            (m) => m.InvoiceDetailComponent,
          ),
        data: {
          roles: [RoleType.Admin, RoleType.Client],
          breadcrumb: 'Invoice Details',
          initialPath: 'invoices',
          mode: 'browser',
          initialBreadcrumbName: 'Invoices',
        },
      },
      //timesheets
      {
        path: 'timesheets',
        canActivate: [RoleGuard],
        resolve: { TimesheetResolver },
        loadComponent: () =>
          import('./pages/timesheets/timesheets.component').then(
            (m) => m.TimesheetsComponent,
          ),
        data: {
          roles: [RoleType.Employee, RoleType.Admin, RoleType.Client],
          breadcrumb: 'Timesheets',
          initialPath: 'timesheets',
          mode: 'browser',
        },
      },
      {
        path: 'timesheets/add',
        canActivate: [RoleGuard],
        resolve: { TimesheetResolver },
        loadComponent: () =>
          import('./pages/timesheets/create-timesheet.component').then(
            (m) => m.CreateTimesheetComponent,
          ),
        data: {
          roles: [RoleType.Employee],
          breadcrumb: 'Create Timesheet',
          initialPath: 'timesheets',
          mode: 'add',
          initialBreadcrumbName: 'Timesheets',
        },
      },
      {
        path: 'timesheets/edit/:worklogId',
        canActivate: [RoleGuard],
        resolve: { TimesheetResolver },
        loadComponent: () =>
          import('./pages/timesheets/edit-timesheet.component').then(
            (m) => m.EditTimesheetComponent,
          ),
        data: {
          roles: [RoleType.Employee],
          breadcrumb: 'Edit Timesheet',
          initialPath: 'timesheets',
          mode: 'edit',
          initialBreadcrumbName: 'Timesheets',
        },
      },

      // {
      //   path: "timesheets/:employeeId",
      //   canActivate: [RoleGuard],
      //   resolve: { TimesheetResolver },
      //   loadComponent: () =>
      //     import("./pages/timesheets/timesheets.component").then(
      //       (m) => m.TimesheetsComponent
      //     ),
      //   data: {
      //     roles: [RoleType.Admin],
      //     breadcrumb: "Employee Timesheets",
      //     initialPath: "timesheets/:employeeId",
      //     mode: "browser",
      //   },
      // },

      // profile
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile/profile.component').then(
            (m) => m.ProfileComponent,
          ),
        data: {
          breadcrumb: 'Profile',
          initialPath: 'profile',
          mode: 'browser',
        },
      },
      {
        path: 'reports/:token',
        loadComponent: () =>
          import('./pages/reports/reports.component').then(
            (m) => m.ReportsComponent,
          ),
        canActivate: [RoleGuard],
        data: { roles: [RoleType.Client] },
      },
    ],
  },

  {
    path: '',
    component: BlankLayoutComponent,
    //canActivate: [NoAuthGuard],
    children: [
      {
        path: 'error',
        loadComponent: () =>
          import('./pages/authentication/error/error.component').then(
            (m) => m.AppErrorComponent,
          ),
      },

      {
        path: 'login',
        loadComponent: () =>
          import('./pages/authentication/side-login/side-login.component').then(
            (m) => m.AppSideLoginComponent,
          ),
      },
      {
        path: 'change-password',
        loadComponent: () =>
          import(
            './pages/authentication/change-password/change-password.component'
          ).then((m) => m.ChangePasswordComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import(
            './pages/authentication/password-reset/password-reset.component'
          ).then((m) => m.PasswordResetComponent),
      },
      {
        path: 'reset-password/:token',
        loadComponent: () =>
          import(
            './pages/authentication/password-reset/password-reset.component'
          ).then((m) => m.PasswordResetComponent),
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'authentication/error',
  },
];
